import { DOMAIN } from 'app/config/environment';
import { isCurrentDomain } from 'app/utils/utils';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LogoGotIt from './assets/logo-got-it.png';
import LogoMakingWaves from './assets/logo-making-waves.png';
import LogoOutsideTheBox from './assets/logo-outside-the-box.png';
import LogoRoots from './assets/logo-roots.png';

type Platform = {
  id: string;
  logo?: string;
  translationKey: string;
  url: string;
};

export type PlatformOption = Omit<Platform, 'translationKey'> & { name: string };

type TranslateFunction = ReturnType<typeof useTranslation>['t'];

type UseDialogSelectPlatform = {
  isOpen: boolean;
  onSelectPlatform: (option: PlatformOption) => void;
  platformOptions: PlatformOption[];
  t: TranslateFunction;
};

/**
 * La lista se construye dentro de una función a propósito: `DOMAIN` no debe leerse en el scope de
 * módulo porque varios tests mockean `app/config/environment` de forma parcial (misma regla que
 * sigue `isPlatformSelectorDomain` en `app/utils/utils.js`).
 */
function getPlatforms(): Platform[] {
  return [
    {
      id: 'primary-roots',
      logo: LogoRoots,
      translationKey: 'login:Primary Roots',
      url: DOMAIN.BYME_DIGITAL,
    },
    {
      id: 'primary-arts-making-waves',
      logo: LogoMakingWaves,
      translationKey: 'login:Primary Arts Making Waves',
      url: DOMAIN.BYME_DIGITAL,
    },
    {
      id: 'primary-outside-the-box',
      logo: LogoOutsideTheBox,
      translationKey: 'login:Primary Outside the Box',
      url: DOMAIN.BILINGUAL_BYME,
    },
    {
      id: 'projects-got-it',
      logo: LogoGotIt,
      translationKey: 'login:Projects Got it!',
      url: DOMAIN.GOT_IT,
    },
    {
      id: 'others',
      translationKey: 'login:Other',
      url: DOMAIN.BILINGUAL_BYME,
    },
  ];
}

function useDialogSelectPlatform(): UseDialogSelectPlatform {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const platformOptions: PlatformOption[] = useMemo(
    () => getPlatforms().map(({ translationKey, ...platform }) => ({ ...platform, name: t(translationKey) })),
    [t]
  );

  /**
   * Si la plataforma seleccionada es la del dominio actual solo se cierra el diálogo,
   * en caso contrario se redirige a la plataforma correspondiente.
   */
  function onSelectPlatform(option: PlatformOption): void {
    if (isCurrentDomain(option.url)) {
      setIsOpen(false);
      return;
    }

    globalThis.location.href = option.url;
  }

  return { isOpen, onSelectPlatform, platformOptions, t };
}

export default useDialogSelectPlatform;
